/* screens/academy/BuyDialog.jsx — ЕДИНСТВЕННАЯ форма покупки курса.
   Modal: выбор тарифа (если их >1) + согласия + телефон. Оферта и Политика —
   ОБЯЗАТЕЛЬНЫ (кнопка оплаты заблокирована, пока оба не отмечены); отзывы и
   рассылки — опциональны. Телефон префиллится из useAuth().user.phone.
   На submit → checkoutCourse(courseId, {tariffId, consents, phone}) → редирект на
   оплату (mock-страница на деве / Робокасса на проде).

   Почему единственная: бэк требует согласия оферты и политики на КАЖДОМ чекауте
   (`consent_required`, views.py). Раньше каталог, модалка тарифов и плеер звали
   checkout напрямую, без согласий, — то есть кнопка «Купить» вне лендинга не
   работала нигде и отвечала общим тостом. Теперь все входы ведут сюда.

   PL-12 — РЕЖИМ ДОПЛАТЫ. У ученика, который уже учится по тарифу, форма раньше
   была тупиком: бэк отвечал `already_enrolled`, и купивший дешёвый тариф оставался
   на нём навсегда (при этом заблокированный урок обещал «перейдите на тариф с
   полной программой»). Теперь бэк отдаёт по каждому тарифу контекст доплаты
   (`upgrade`), и форма показывает СПИСОК СТАРШИХ тарифов с ценой ДОПЛАТЫ (разница
   цен), свой текущий тариф — отдельной строкой. Тарифы дешевле текущего в список
   не попадают вовсе: их нельзя купить, и предлагать их значило бы вести человека
   в отказ. */

import React from 'react';
import { Modal } from '../../components/ui/Modal.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Checkbox, Field, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import * as api from '../../lib/api/academy.js';
import { academyErrorText } from '../../lib/academy/errorCodes.js';
import { money, homeworkModeLabel, accessDaysLabel, resolvePreselectedTariff }
  from '../../lib/academy/pricing.js';

const { useState, useEffect, useMemo } = React;

/** Контекст доплаты по списку тарифов (PL-12).
 *
 *  `upgrade` на тарифе приходит от `GET courses/<id>/tariffs/`: null — доплата не
 *  про этого пользователя (доступа нет или он истёк → обычная покупка/продление
 *  по полной цене); объект — ученик учится по тарифу этого курса.
 *
 *  Экспортируется ради читаемости и юнит-тестов: это единственное место, где
 *  решается, что покупателю вообще предлагать. */
export function upgradeContext(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const annotated = list.some((t) => t && t.upgrade);
  if (!annotated) return { mode: false, current: null, offers: list, noBase: false };
  return {
    mode: true,
    current: list.find((t) => t.upgrade && t.upgrade.is_current) || null,
    // Только то, что реально можно оплатить: старшие тарифы с положительной
    // разницей. Текущий и более дешёвые бэк пометил reason='same_tariff'/'downgrade'.
    offers: list.filter((t) => t.upgrade && t.upgrade.available),
    // Доступ есть, а тарифа у записи нет (записал методист вручную): доплату
    // считать не от чего. Причину называем словами — иначе человек читал бы
    // «у вас старший тариф», которого у него нет вовсе.
    noBase: list.every((t) => t.upgrade && t.upgrade.reason === 'upgrade_no_base'),
  };
}

/** Сколько спишут за этот тариф: в режиме доплаты — разницу, иначе полную цену. */
export function payableAmount(tariff, upgradeMode) {
  if (!tariff) return null;
  if (upgradeMode && tariff.upgrade && tariff.upgrade.available) {
    return tariff.upgrade.price_rub;
  }
  return tariff.price_rub;
}

// Подписи тарифа (цена, режим ДЗ, срок) — общие с блоком «Тарифы» лендинга:
// покупатель видит на кнопке и в форме оплаты ровно один и тот же текст (AD-9).
function BuyDialog({ courseId, courseTitle, coursePrice, tariffs, initialTariffId, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const propList = Array.isArray(tariffs) ? tariffs : [];
  // Контекст доплаты приходит от эндпоинта тарифов. Три из четырёх входов в форму
  // уже зовут его сами и передают готовый список; ЛЕНДИНГ отдаёт тарифы своим
  // ПУБЛИЧНЫМ пейлоадом (он анонимный и о доплате знать не может) — тогда
  // дозапрашиваем контекст здесь. Иначе доплата работала бы в трёх входах из
  // четырёх, а на четвёртом молча предлагала бы вторую полную оплату.
  const [ctxRows, setCtxRows] = useState(null);
  const needsCtx = propList.length > 0
    && propList.some((t) => t && t.upgrade === undefined);
  useEffect(() => {
    if (!needsCtx || ctxRows) return undefined;
    let alive = true;
    api.getCourseTariffs(courseId)
      .then((rows) => { if (alive && Array.isArray(rows)) setCtxRows(rows); })
      // Не смогли — работаем как раньше (обычная покупка). Молчим: это уточнение
      // условий, а не действие пользователя, и красный тост тут ничего не решает.
      .catch(() => {});
    return () => { alive = false; };
  }, [needsCtx, ctxRows, courseId]);

  const rows = ctxRows || propList;
  const upgrade = useMemo(() => upgradeContext(rows), [rows]);
  const upgradeMode = upgrade.mode;
  const list = upgrade.offers;
  const multi = list.length > 1;
  // Тариф, выбранный на лендинге, приезжает выбранным и сюда — иначе покупатель
  // жал «Купить · 5 900 ₽» на карточке, а в форме видел первый тариф из списка.
  // Если тарифа больше нет в продаже (повторная оплата брошенного заказа, а
  // методист деактивировал тариф), предвыбор МОЛЧА падал на первый в списке, и
  // покупатель брал не то, что бросил. Теперь подмена названа словами, а при
  // нескольких действующих тарифах выбор делает человек.
  const preselect = resolvePreselectedTariff(list, initialTariffId);
  const [tariffId, setTariffId] = useState(preselect.tariffId);
  const lostTariff = preselect.lost && !upgradeMode;
  const [consents, setConsents] = useState({ offer: false, privacy: false, reviews: false, mailings: false });
  const [phone, setPhone] = useState((user && user.phone) || '');
  const needsPhone = !((user && user.phone) || '').trim();
  const [phoneErr, setPhoneErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Контекст доплаты приезжает ПОСЛЕ первого рендера (см. эффект выше), и выбор,
  // сделанный по старому списку, может оказаться неоплачиваемым (например,
  // предвыбран текущий тариф ученика). Тогда переводим выбор на первую доступную
  // доплату — иначе кнопка «Доплатить» ушла бы в отказ `same_tariff`.
  useEffect(() => {
    if (!upgradeMode) return;
    if (list.some((t) => t.id === tariffId)) return;
    setTariffId(list.length ? list[0].id : null);
  }, [upgradeMode, list, tariffId]);

  const setConsent = (k) => (e) => setConsents((c) => ({ ...c, [k]: e.target.checked }));

  // Обязательные: оферта + политика. Без них кнопку не активируем (бэк всё равно
  // отвергнет запрос без них — code='consent_required', #9; это фронт-зеркало гейта).
  // Курс продаётся по тарифам — без выбранного тарифа платить нечему (бэк
  // ответит 400 tariff_required). Это состояние возникает ровно в одном случае:
  // прежний тариф снят с продажи и предвыбор сознательно не сделан.
  // В режиме доплаты пустой список — это «старших тарифов нет»: платить нечему.
  const canPay = consents.offer && consents.privacy && !busy
    && !(list.length > 0 && !tariffId)
    && !(upgradeMode && !list.length);
  const selected = list.find((t) => t.id === tariffId) || null;
  // Курс без тарифов продаётся по единой цене — сумму берём из курса, иначе
  // покупатель видел бы кнопку «Оплатить» без числа и не знал, сколько спишут.
  // Но если курс продаётся ПО ТАРИФАМ и тариф ещё не выбран, цена курса на
  // кнопке была бы заведомо чужой цифрой — тогда сумму не называем вовсе.
  // В режиме доплаты сумма — РАЗНИЦА цен (её же спишет чекаут).
  const amount = selected ? payableAmount(selected, upgradeMode)
    : (list.length || upgradeMode ? null
      : (coursePrice != null ? coursePrice : null));

  const pay = async () => {
    if (!canPay) return;
    // Спрашиваем ФОРМОЙ, а не отказом API: бэкенд отсутствие номера не блокирует
    // (иначе любой вызов мимо нашей формы означал бы «клиент не может
    // заплатить»), поэтому обязательность живёт здесь, у видимого поля.
    if (needsPhone && !phone.trim()) {
      setPhoneErr('Укажите номер телефона — он нужен для связи по оплате.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.checkoutCourse(courseId, {
        tariffId: tariffId || undefined,
        consents: {
          offer: consents.offer, privacy: consents.privacy,
          reviews: consents.reviews, mailings: consents.mailings,
        },
        phone: phone ? phone.trim() : undefined,
      });
      // Редирект на оплату: Робокасса (прод) / mock-страница академии (дев).
      window.location.href = r.redirect_url;
    } catch (e) {
      const phoneMsg = e && (e.phone && e.phone[0]
        || (String(e.code || '').startsWith('phone') ? e.detail : ''));
      if (phoneMsg) {
        setPhoneErr(phoneMsg);
        setBusy(false);
        return;
      }
      // Доменные коды оплаты (consent_required / bad_price / already_enrolled /
      // tariff_*) объясняются человеческим текстом, а не сырым detail (ST-6).
      toast.show(academyErrorText(e, 'Не удалось начать оплату'), 'danger');
      setBusy(false);
    }
  };

  const payVerb = upgradeMode ? 'Доплатить' : 'Оплатить';
  const payLabel = amount != null ? `${payVerb} · ${money(amount)}` : payVerb;
  const currentTitle = upgrade.current ? upgrade.current.title : '';

  return (
    <Modal open size="lg" onClose={onClose}
      title={`${upgradeMode ? 'Доплата' : 'Оформление'} · ${courseTitle || 'курс'}`}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button variant="primary" loading={busy} disabled={!canPay} onClick={pay}>{payLabel}</Button>
        </>
      )}>
      <div className="cl-buy">
        {/* Тариф брошенного заказа снят с продажи — говорим об этом до оплаты,
            а не подсовываем молча другой. */}
        {lostTariff && (
          <div className="cl-buy__notice" role="status">
            <Icon name="alert-circle" size={16} />
            <span>
              {multi
                ? 'Тариф, который вы выбирали, больше не продаётся — выберите другой.'
                : `Тариф, который вы выбирали, больше не продаётся. Оформление — по действующему тарифу${selected ? ` «${selected.title}»` : ''}.`}
            </span>
          </div>
        )}
        {/* PL-12: доплата. Называем текущий тариф и правило списания — покупатель
            обязан понимать, за что платит, до нажатия кнопки. */}
        {upgradeMode && (
          <>
            {upgrade.current && (
              <div className="cl-buy__single">
                <span className="cl-buy__single-title">
                  Ваш тариф{currentTitle ? ` · ${currentTitle}` : ''}
                </span>
                <span className="cl-buy__single-price num">{money(upgrade.current.price_rub)}</span>
              </div>
            )}
            {list.length > 0 && (
              <p className="t-caption text-mute cl-buy__single-feats">
                Спишем только разницу в цене — второй раз за курс платить не нужно.
                Пройденное и сроки сохраняются.
              </p>
            )}
            {!list.length && (
              <div className="cl-buy__notice" role="status">
                <Icon name="alert-circle" size={16} />
                <span>
                  {upgrade.noBase
                    ? 'Доступ к курсу у вас уже есть, но он выдан без тарифа — доплату считать не от чего. Напишите в поддержку, если нужен другой тариф.'
                    : 'Доплачивать не за что: у вас действует старший из доступных тарифов. Курс открыт в разделе «Обучение».'}
                </span>
              </div>
            )}
          </>
        )}
        {/* выбор тарифа (только если их несколько) */}
        {multi && (
          <div className="cl-buy__tariffs" role="radiogroup"
            aria-label={upgradeMode ? 'Тариф для доплаты' : 'Тариф'}>
            {list.map((t) => {
              const active = t.id === tariffId;
              const diff = payableAmount(t, upgradeMode);
              return (
                <button key={t.id} type="button" role="radio" aria-checked={active}
                  className={'cl-buy-tariff' + (active ? ' is-active' : '')}
                  onClick={() => setTariffId(t.id)}>
                  <span className="cl-buy-tariff__mark" aria-hidden="true">
                    {active && <Icon name="check" size={12} />}
                  </span>
                  <span className="cl-buy-tariff__body">
                    <span className="cl-buy-tariff__title">{t.title}</span>
                    <span className="cl-buy-tariff__feats t-caption text-mute">
                      {t.includes_all_modules ? 'Все модули' : `${t.module_count} модуль(ей)`}
                      {' · '}{homeworkModeLabel(t)}
                      {' · '}{accessDaysLabel(t).toLowerCase()}
                      {/* На доплате в строке две цифры: сколько спишут (крупно) и
                          полная цена тарифа — иначе разницу читают как цену тарифа. */}
                      {upgradeMode ? ` · тариф целиком ${money(t.price_rub)}` : ''}
                    </span>
                  </span>
                  {/* `num` — только на цифрах (табличные цифры); слово «доплата»
                      в тот же span попадать не должно. */}
                  <span className="cl-buy-tariff__price">
                    {upgradeMode
                      ? <>доплата <span className="num">{money(diff)}</span></>
                      : <span className="num">{money(t.price_rub)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {/* Единственная доступная доплата — показываем её строкой, без выбора. */}
        {upgradeMode && !multi && selected && (
          <div className="cl-buy__single">
            <span className="cl-buy__single-title">Доплата до «{selected.title}»</span>
            <span className="cl-buy__single-price num">{money(amount)}</span>
          </div>
        )}
        {/* Обычная покупка. В режиме доплаты строку рисует блок выше — иначе одна
            и та же сумма печаталась бы дважды, под разными подписями. */}
        {!multi && !upgradeMode && (selected || amount != null) && (
          <div className="cl-buy__single">
            <span className="cl-buy__single-title">{selected ? selected.title : courseTitle}</span>
            <span className="cl-buy__single-price num">{money(amount)}</span>
          </div>
        )}
        {!multi && selected && (
          <p className="t-caption text-mute cl-buy__single-feats">
            {selected.includes_all_modules ? 'Все модули курса' : `${selected.module_count} модуль(ей)`}
            {' · '}{homeworkModeLabel(selected)}
            {' · '}{accessDaysLabel(selected).toLowerCase()}
            {selected.grants_certificate ? ' · сертификат по завершении' : ''}
          </p>
        )}

{/* Телефон спрашиваем один раз: у кого он уже сохранён, поля нет вовсе.
            Обязательность и разбор формата — на бэке (accounts/phones.py), сюда
            приходит готовый к показу текст отказа; своей валидации не заводим,
            иначе она разойдётся с серверной. */}
        {needsPhone && (
          <Field label="Телефон" error={phoneErr} required
            hint="Российский можно без кода страны; для остальных укажите код, например +33 6 12 34 56 78">
            <TextInput type="tel" inputMode="tel" value={phone} placeholder="+7 900 000-00-00"
              onChange={(e) => { setPhone(e.target.value); if (phoneErr) setPhoneErr(''); }} />
          </Field>
        )}

        {/* согласия: оферта + политика обязательны; отзывы + рассылки опц. */}
        <div className="cl-buy__consents">
          <label className="checkbox">
            <input type="checkbox" className="checkbox__input" checked={consents.offer} onChange={setConsent('offer')} />
            <span className="checkbox__box" aria-hidden="true"><Icon name="check" size={12} /></span>
            <span className="checkbox__label">
              Принимаю условия <a href="/legal/offer" target="_blank" rel="noopener noreferrer">оферты</a>
              <span className="cl-buy__req" aria-hidden="true"> *</span>
            </span>
          </label>
          <label className="checkbox">
            <input type="checkbox" className="checkbox__input" checked={consents.privacy} onChange={setConsent('privacy')} />
            <span className="checkbox__box" aria-hidden="true"><Icon name="check" size={12} /></span>
            <span className="checkbox__label">
              Согласен с <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">политикой конфиденциальности</a>
              <span className="cl-buy__req" aria-hidden="true"> *</span>
            </span>
          </label>
          <Checkbox label="Согласен оставить отзыв о курсе" checked={consents.reviews} onChange={setConsent('reviews')} />
          <Checkbox label="Хочу получать новости и рассылки" checked={consents.mailings} onChange={setConsent('mailings')} />
        </div>

        {/* Подсказка обязана называть ТО, чего не хватает: с невыбранным тарифом
            «отметьте согласия» отправляло бы человека чинить не то. */}
        {!canPay && !busy && (
          <p className="t-caption text-mute cl-buy__hint">
            <Icon name="info" size={13} />{' '}
            {upgradeMode && !list.length
              ? 'Оплачивать нечего — закройте окно.'
              : (list.length > 0 && !tariffId
                ? `Выберите тариф${upgradeMode ? ' для доплаты' : ''}, чтобы продолжить.`
                : 'Отметьте оферту и политику конфиденциальности, чтобы продолжить.')}
          </p>
        )}
      </div>
    </Modal>
  );
}

export { BuyDialog };
